<template>
	<div class="command-categories" style="height: 116px; min-height: 116px; max-height: 116px">
		<div v-for="category in categories" :key="category.title" class="category category-feature">
			<div class="category-actions feature-layout-grid" :style="getFeatureGridStyle(category)" :data-feature-category="category.title">
				<div
					v-for="item in getFeatureLayoutItems(category)"
					:key="item.action.key"
					class="feature-command"
					:class="{
						active: isFeatureActionActive(item.action),
						'feature-command-large': item.size === 'large',
						'feature-command-small': item.size === 'small',
					}"
					:style="getFeatureCellStyle(item)"
					:data-feature-key="item.action.key"
					:data-feature-size="item.size"
					:data-feature-mode="item.mode"
					:data-feature-row-span="String(item.rowSpan)"
				>
					<DropdownButton
						v-if="item.action.key === 'feature-corner'"
						:is-active="isFeatureCornerActive"
						:disabled="Boolean(activeFeatureCornerOption.disabled)"
						:icon="activeFeatureCornerOption.icon"
						:label="activeFeatureCornerOption.label"
						:hint="{
							image: activeFeatureCornerOption.hintImage,
							desc: activeFeatureCornerOption.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="featureCornerDropdownOptions"
						@select="handleFeatureCornerSelect"
						@click="handleFeatureCornerClick"
					/>
					<DropdownButton
						v-else-if="item.action.key === 'feature-pattern'"
						:is-active="isFeaturePatternActive"
						:disabled="Boolean(activeFeaturePatternOption.disabled)"
						:icon="activeFeaturePatternOption.icon"
						:label="activeFeaturePatternOption.label"
						:hint="{
							image: activeFeaturePatternOption.hintImage,
							desc: activeFeaturePatternOption.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="featurePatternDropdownOptions"
						@select="handleFeaturePatternSelect"
						@click="handleFeaturePatternClick"
					/>
					<DropdownButton
						v-else-if="item.action.key === 'reference-geometry'"
						:is-active="isFeatureReferenceActive"
						:disabled="Boolean(activeFeatureReferenceOption.disabled)"
						:icon="activeFeatureReferenceOption.icon"
						:label="activeFeatureReferenceOption.label"
						:hint="{
							image: activeFeatureReferenceOption.hintImage,
							desc: activeFeatureReferenceOption.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="featureReferenceDropdownOptions"
						@select="handleFeatureReferenceSelect"
						@click="handleFeatureReferenceClick"
					/>
					<DropdownButton
						v-else
						:is-active="isCommandActive(item.action.command)"
						:disabled="Boolean(item.action.disabled)"
						:icon="item.action.icon"
						:label="item.action.label"
						:hint="{
							image: item.action.hintImage,
							desc: item.action.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						@click="handleAction(item.action)"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, h, ref, watch, type CSSProperties } from "vue";
	import { storeToRefs } from "pinia";
	import type { DropdownOption } from "naive-ui";
	import DropdownButton from "@/components/common/DropdownButton.vue";
	import SvgIcon from "@/components/common/SvgIcon.vue";
	import { useToolbarCommandStore } from "@/stores/toolbar-command";
	import featureExtrudeBossIcon from "@/assets/icon/feature_extrude_boss.svg?raw";
	import featureRevolveBossIcon from "@/assets/icon/feature_revolve_boss.svg?raw";
	import featureSweepBossIcon from "@/assets/icon/feature_sweep_boss.svg?raw";
	import featureLoftBossIcon from "@/assets/icon/feature_loft_boss.svg?raw";
	import featureBoundaryBossIcon from "@/assets/icon/feature_boundary_boss.svg?raw";
	import featureExtrudeCutIcon from "@/assets/icon/feature_extrude_cut.svg?raw";
	import featureRevolveCutIcon from "@/assets/icon/feature_revolve_cut.svg?raw";
	import featureHoleWizardIcon from "@/assets/icon/feature_hole_wizard.svg?raw";
	import featureSweepCutIcon from "@/assets/icon/feature_sweep_cut.svg?raw";
	import featureLoftCutIcon from "@/assets/icon/feature_loft_cut.svg?raw";
	import featureBoundaryCutIcon from "@/assets/icon/feature_boundary_cut.svg?raw";
	import featureFilletIcon from "@/assets/icon/feature_fillet.svg?raw";
	import featureChamferIcon from "@/assets/icon/feature_chamfer.svg?raw";
	import featurePatternLinearSwIcon from "@/assets/icon/feature_pattern_linear_sw.svg?raw";
	import featurePatternCircularIcon from "@/assets/icon/feature_pattern_circular.svg?raw";
	import featurePatternCurveDrivenIcon from "@/assets/icon/feature_pattern_curve_driven.svg?raw";
	import featurePatternSketchDrivenIcon from "@/assets/icon/feature_pattern_sketch_driven.svg?raw";
	import featurePatternTableDrivenIcon from "@/assets/icon/feature_pattern_table_driven.svg?raw";
	import featurePatternFillIcon from "@/assets/icon/feature_pattern_fill.svg?raw";
	import featurePatternVariableIcon from "@/assets/icon/feature_pattern_variable.svg?raw";
	import featureRibIcon from "@/assets/icon/feature_rib.svg?raw";
	import featureWrapIcon from "@/assets/icon/feature_wrap.svg?raw";
	import featureDraftIcon from "@/assets/icon/feature_draft.svg?raw";
	import featureIntersectIcon from "@/assets/icon/feature_intersect.svg?raw";
	import featureShellIcon from "@/assets/icon/feature_shell.svg?raw";
	import featureMirrorIcon from "@/assets/icon/feature_mirror.svg?raw";
	import featureReferenceGeometrySwIcon from "@/assets/icon/feature_reference_geometry_sw.svg?raw";
	import featureReferencePlaneIcon from "@/assets/icon/feature_reference_plane.svg?raw";
	import featureCurveIcon from "@/assets/icon/feature_curve.svg?raw";
	import featureInstant3dIcon from "@/assets/icon/feature_instant3d.svg?raw";
	import sketchSnapPointIcon from "@/assets/icon/sketch_snap_point.svg?raw";
	import axisIcon from "@/assets/icon/axis.svg?raw";

	interface ToolbarAction {
		key: string;
		label: string;
		icon: string;
		command?: string;
		hint?: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface ToolbarOperation {
		name: string;
		hint: string;
	}

	type FeatureLayoutCellSize = "small" | "large";
	type FeatureLayoutCellMode = "row" | "column";
	type FeatureCornerModeKey = "fillet" | "chamfer";
	type FeatureReferenceModeKey = "geometry-reference-plane" | "geometry-reference-axis" | "geometry-reference-point";
	type FeaturePatternModeKey =
		| "pattern-grid"
		| "pattern-circular"
		| "pattern-mirror"
		| "pattern-curve-driven"
		| "pattern-sketch-driven"
		| "pattern-table-driven"
		| "pattern-fill"
		| "pattern-variable";

	interface FeatureCornerModeOption {
		mode: FeatureCornerModeKey;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface FeaturePatternModeOption {
		mode: FeaturePatternModeKey;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface FeatureReferenceModeOption {
		mode: FeatureReferenceModeKey;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface FeatureLayoutCell {
		key: string;
		column: number;
		row: number;
		rowSpan?: number;
		size?: FeatureLayoutCellSize;
		mode?: FeatureLayoutCellMode;
	}

	interface FeatureLayout {
		columns: number;
		rows: number;
		cells: FeatureLayoutCell[];
	}

	interface FeatureLayoutItem {
		action: ToolbarAction;
		column: number;
		row: number;
		rowSpan: number;
		size: FeatureLayoutCellSize;
		mode: FeatureLayoutCellMode;
	}

	interface ToolbarCategory {
		title: string;
		actions: ToolbarAction[];
		featureLayout: FeatureLayout;
	}

	const emit = defineEmits<{
		(e: "command", commandType: string): void;
		(e: "operationChange", operation: ToolbarOperation): void;
	}>();

	const toolbarCommandStore = useToolbarCommandStore();
	const { activeCommands } = storeToRefs(toolbarCommandStore);

	const featureCornerModeOptions: FeatureCornerModeOption[] = [
		{
			mode: "fillet",
			label: "圆角",
			icon: featureFilletIcon,
			command: "fillet",
			hint: "沿实体中的一条或多条边线来创建圆角面。在'属性'中选择圆角类型，然后设定与该类型相应的其他选项。",
			hintImage: "/images/operation/feature-fillet.gif",
		},
		{
			mode: "chamfer",
			label: "倒角",
			icon: featureChamferIcon,
			command: "chamfer",
			hint: "沿实体中的一条或多条边线来创建倒角面。在'属性'中选择倒角类型，然后设定与该类型相应的其他选项。",
			hintImage: "/images/operation/feature-chamfer.gif",
		},
	];

	const featureCornerModeMap = new Map(featureCornerModeOptions.map(option => [option.mode, option]));
	const featureCornerCommandMap = new Map(featureCornerModeOptions.map(option => [option.command, option]));
	const activeFeatureCornerMode = ref<FeatureCornerModeKey>("fillet");
	const activeFeatureCornerOption = computed(() => featureCornerModeMap.get(activeFeatureCornerMode.value) ?? featureCornerModeOptions[0]);
	const isFeatureCornerActive = computed(() => activeCommands.value.some(command => command === "fillet" || command === "chamfer"));
	const featureCornerDropdownOptions = computed<DropdownOption[]>(() =>
		featureCornerModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const featurePatternModeOptions: FeaturePatternModeOption[] = [
		{
			mode: "pattern-grid",
			label: "线性阵列",
			icon: featurePatternLinearSwIcon,
			command: "pattern-grid",
			hint: "在一个或两个线性方向上阵列特征、面和实体。您可以选择参考几何图形来定义阵列。您也可以跳过实例。",
		},
		{
			mode: "pattern-circular",
			label: "圆周阵列",
			icon: featurePatternCircularIcon,
			command: "pattern-circular",
			hint: "在圆周上阵列特征、面和实体。您可以选择参考几何图形来定义阵列。您也可以跳过实例。",
		},
		{
			mode: "pattern-mirror",
			label: "镜向",
			icon: featureMirrorIcon,
			command: "pattern-mirror",
			hint: "镜向阵列",
		},
		{
			mode: "pattern-curve-driven",
			label: "曲线驱动的阵列",
			icon: featurePatternCurveDrivenIcon,
			command: "pattern-curve-driven",
			hint: "曲线驱动的阵列",
		},
		{
			mode: "pattern-sketch-driven",
			label: "草图驱动的阵列",
			icon: featurePatternSketchDrivenIcon,
			command: "pattern-sketch-driven",
			hint: "草图驱动的阵列",
		},
		{
			mode: "pattern-table-driven",
			label: "表格驱动的阵列",
			icon: featurePatternTableDrivenIcon,
			command: "pattern-table-driven",
			hint: "表格驱动的阵列",
		},
		{
			mode: "pattern-fill",
			label: "填充阵列",
			icon: featurePatternFillIcon,
			command: "pattern-fill",
			hint: "填充阵列",
		},
		{
			mode: "pattern-variable",
			label: "变量阵列",
			icon: featurePatternVariableIcon,
			command: "pattern-variable",
			hint: "变量阵列",
		},
	];

	const featurePatternModeMap = new Map(featurePatternModeOptions.map(option => [option.mode, option]));
	const featurePatternCommandMap = new Map(featurePatternModeOptions.map(option => [option.command, option]));
	const featurePatternCommandSet = new Set(featurePatternModeOptions.map(option => option.command));
	const activeFeaturePatternMode = ref<FeaturePatternModeKey>("pattern-grid");
	const activeFeaturePatternOption = computed(() => featurePatternModeMap.get(activeFeaturePatternMode.value) ?? featurePatternModeOptions[0]);
	const isFeaturePatternActive = computed(() => activeCommands.value.some(command => featurePatternCommandSet.has(command)));
	const featurePatternDropdownOptions = computed<DropdownOption[]>(() =>
		featurePatternModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			disabled: option.disabled,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const featureReferenceModeOptions: FeatureReferenceModeOption[] = [
		{
			mode: "geometry-reference-point",
			label: "基准点",
			icon: sketchSnapPointIcon,
			command: "geometry-reference-point",
			hint: "创建基准点",
		},
		{
			mode: "geometry-reference-axis",
			label: "基准轴",
			icon: axisIcon,
			command: "geometry-reference-axis",
			hint: "创建基准轴",
		},
		{
			mode: "geometry-reference-plane",
			label: "基准面",
			icon: featureReferencePlaneIcon,
			command: "geometry-reference-plane",
			hint: "创建基准面",
		},
	];

	const featureReferenceModeMap = new Map(featureReferenceModeOptions.map(option => [option.mode, option]));
	const featureReferenceCommandMap = new Map(featureReferenceModeOptions.map(option => [option.command, option]));
	const featureReferenceCommandSet = new Set(featureReferenceModeOptions.map(option => option.command));
	const activeFeatureReferenceMode = ref<FeatureReferenceModeKey>("geometry-reference-plane");
	const activeFeatureReferenceOption = computed(() => featureReferenceModeMap.get(activeFeatureReferenceMode.value) ?? featureReferenceModeOptions[0]);
	const isFeatureReferenceActive = computed(() =>
		activeCommands.value.some(command => featureReferenceCommandSet.has(command) || command === "reference-plane" || command === "reference-axis")
	);
	const featureReferenceDropdownOptions = computed<DropdownOption[]>(() =>
		featureReferenceModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			disabled: option.disabled,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const categories: ToolbarCategory[] = [
		{
			title: "造型",
			actions: [
				{
					key: "extrude-boss",
					label: "拉伸凸台/基体",
					icon: featureExtrudeBossIcon,
					command: "extrude",
					hint: "通过向一个或两个方向拉伸草图或选定草图轮廓来创建实体特征。",
					hintImage: "/images/operation/feature-extrude-boss.gif",
				},
				{
					key: "revolve-boss",
					label: "旋转凸台/基体",
					icon: featureRevolveBossIcon,
					command: "revolve-boss",
					hint: "绕轴心旋转草图或所选草图轮廓来创建实体特征。",
					hintImage: "/images/operation/feature-revolve-boss.gif",
				},
				{
					key: "sweep-boss",
					label: "扫描",
					icon: featureSweepBossIcon,
					command: "sweep-boss",
					hint: "沿开环或闭合路径通过扫描闭合轮廓来创建实体特征。您可以使用'属性'中的选项，如引导线、轮廓方向选项和扭转来创建各种形状。",
					disabled: true,
				},
				{
					key: "loft-boss",
					label: "放样凸台/基体",
					icon: featureLoftBossIcon,
					command: "loft-boss",
					hint: "在两个或多个轮廓之间添加材料来创建实体特征。第一个、最后一个轮廓可以是点。",
					disabled: true,
				},
				{
					key: "boundary-boss",
					label: "边界凸台/基体",
					icon: featureBoundaryBossIcon,
					command: "boundary-boss",
					hint: "在双向轮廓之间添加材料以创建实体特征。在'属性'中指定定义边界特征形状的草图实体，并控制实体如何影响设计。",
					disabled: true,
				},
			],
			featureLayout: {
				columns: 3,
				rows: 3,
				cells: [
					{ key: "extrude-boss", column: 1, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "revolve-boss", column: 2, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "sweep-boss", column: 3, row: 1, size: "small", mode: "row" },
					{ key: "loft-boss", column: 3, row: 2, size: "small", mode: "row" },
					{ key: "boundary-boss", column: 3, row: 3, size: "small", mode: "row" },
				],
			},
		},
		{
			title: "切除",
			actions: [
				{
					key: "extrude-cut",
					label: "拉伸切除",
					icon: featureExtrudeCutIcon,
					command: "extrude-cut",
					hint: "通过在一个或两个方向上拉伸草图轮廓来切除实体模型。",
					hintImage: "/images/operation/feature-extrude-cut.gif",
				},
				{
					key: "revolve-cut",
					label: "旋转切除",
					icon: featureRevolveCutIcon,
					command: "revolve-cut",
					hint: "通过围绕坐标轴旋转草图轮廓来切除实体模型。",
					hintImage: "/images/operation/feature-revolve-cut.gif",
				},
				{
					key: "hole-wizard",
					label: "异型孔向导",
					icon: featureHoleWizardIcon,
					command: "hole-wizard",
					hint: "插入各种类型的自定义孔，例如沉头孔、锥形沉头孔或螺纹孔。",
					disabled: true,
				},
				{
					key: "sweep-cut",
					label: "扫描切除",
					icon: featureSweepCutIcon,
					command: "sweep-cut",
					hint: "通过扫描沿开环或闭合路径的闭合轮廓来切除实体模型。常见用途是绕圆柱实体创建切除。",
					disabled: true,
				},
				{
					key: "loft-cut",
					label: "放样切除",
					icon: featureLoftCutIcon,
					command: "loft-cut",
					hint: "通过移除两个或多个轮廓之间的材料来切除实体模型。第一个、最后一个轮廓可以是点。",
					disabled: true,
				},
				{
					key: "boundary-cut",
					label: "边界切除",
					icon: featureBoundaryCutIcon,
					command: "boundary-cut",
					hint: "通过在两个方向上移除轮廓之间的材料来切除实体模型。",
					disabled: true,
				},
			],
			featureLayout: {
				columns: 4,
				rows: 3,
				cells: [
					{ key: "extrude-cut", column: 1, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "revolve-cut", column: 2, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "hole-wizard", column: 3, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "sweep-cut", column: 4, row: 1, size: "small", mode: "row" },
					{ key: "loft-cut", column: 4, row: 2, size: "small", mode: "row" },
					{ key: "boundary-cut", column: 4, row: 3, size: "small", mode: "row" },
				],
			},
		},
		{
			title: "圆角阵列与成形",
			actions: [
				{
					key: "feature-corner",
					label: "圆角",
					icon: featureFilletIcon,
					command: "fillet",
					hint: "沿实体或曲面特征中的一条或多条边线来创建圆形内部或外部面。在'属性'中选择圆角类型，然后设定与该类型相应的其他选项。",
				},
				{
					key: "feature-pattern",
					label: "线性阵列",
					icon: featurePatternLinearSwIcon,
					command: "pattern-grid",
					hint: "在一个或两个线性方向上阵列特征、面和实体。您可以选择参考几何图形来定义阵列。您也可以跳过实例。",
				},
				{
					key: "rib",
					label: "筋",
					icon: featureRibIcon,
					command: "rib",
					hint: "通过在一个或多个草图轮廓和现有零件之间添加材料为实体添加薄壁支撑。您也可以创建带拔模的筋特征。",
					disabled: false,
				},
				{
					key: "draft",
					label: "拔模",
					icon: featureDraftIcon,
					command: "draft",
					hint: "使用中性面或分型线按所指定的角度削尖模型面。拔模经常用于使模制零件更容易从模具移除。",
					disabled: true,
				},
				{
					key: "shell",
					label: "抽壳",
					icon: featureShellIcon,
					command: "shell",
					hint: "镂空零件，保持所选面打开，然后在剩余面上创建薄壁特征。如果您没有选择面，则壳体将创建闭合的空心模型。",
					disabled: false,
				},
				{
					key: "wrap",
					label: "包覆",
					icon: featureWrapIcon,
					command: "wrap",
					hint: "将闭合的草图轮廓包覆到面上。",
					disabled: true,
				},
				{
					key: "intersect",
					label: "相交",
					icon: featureIntersectIcon,
					command: "intersect",
					hint: "相交曲面、平面和实体，以创建卷。",
					disabled: true,
				},
				{
					key: "mirror",
					label: "镜向",
					icon: featureMirrorIcon,
					command: "mirror",
					hint: "围绕基准面或平面镜向特征、面和实体。",
					disabled: true,
				},
			],
			featureLayout: {
				columns: 4,
				rows: 3,
				cells: [
					{ key: "feature-corner", column: 1, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "feature-pattern", column: 2, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "rib", column: 3, row: 1, size: "small", mode: "row" },
					{ key: "wrap", column: 4, row: 1, size: "small", mode: "row" },
					{ key: "draft", column: 3, row: 2, size: "small", mode: "row" },
					{ key: "intersect", column: 4, row: 2, size: "small", mode: "row" },
					{ key: "shell", column: 3, row: 3, size: "small", mode: "row" },
					{ key: "mirror", column: 4, row: 3, size: "small", mode: "row" },
				],
			},
		},
		{
			title: "参考",
			actions: [
				{
					key: "reference-geometry",
					label: "参考几何体",
					icon: featureReferenceGeometrySwIcon,
					command: "reference-plane",
					hint: "参考几何体指令",
				},
				{
					key: "curve",
					label: "曲线",
					icon: featureCurveIcon,
					command: "curve",
					hint: "曲线指令",
					disabled: true,
				},
				{
					key: "instant3d",
					label: "Instant3D",
					icon: featureInstant3dIcon,
					command: "instant3d",
					hint: "启用拖动控标、尺寸及几何体来动态修改特征。屏幕上的标尺帮助您准确测量线性和角度调整。",
					disabled: true,
				},
			],
			featureLayout: {
				columns: 3,
				rows: 2,
				cells: [
					{ key: "reference-geometry", column: 1, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "curve", column: 2, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "instant3d", column: 3, row: 1, rowSpan: 2, size: "large", mode: "column" },
				],
			},
		},
	];

	watch(
		activeCommands,
		commands => {
			if (commands.includes("chamfer")) {
				activeFeatureCornerMode.value = "chamfer";
				return;
			}
			if (commands.includes("fillet")) {
				activeFeatureCornerMode.value = "fillet";
			}
		},
		{ deep: true, immediate: true }
	);

	watch(
		activeCommands,
		commands => {
			const matched = commands.find(command => featurePatternCommandSet.has(command));
			if (!matched) {
				return;
			}
			const option = featurePatternCommandMap.get(matched);
			if (option) {
				activeFeaturePatternMode.value = option.mode;
			}
		},
		{ deep: true, immediate: true }
	);

	watch(
		activeCommands,
		commands => {
			const matched = commands.find(command => featureReferenceCommandSet.has(command));
			if (matched) {
				const option = featureReferenceCommandMap.get(matched);
				if (option) {
					activeFeatureReferenceMode.value = option.mode;
				}
				return;
			}
			if (commands.includes("reference-axis")) {
				activeFeatureReferenceMode.value = "geometry-reference-axis";
				return;
			}
			if (commands.includes("reference-plane")) {
				activeFeatureReferenceMode.value = "geometry-reference-plane";
			}
		},
		{ deep: true, immediate: true }
	);

	function getFeatureLayoutItems(category: ToolbarCategory): FeatureLayoutItem[] {
		return category.featureLayout.cells
			.map(cell => {
				const action = category.actions.find(candidate => candidate.key === cell.key);
				if (!action) {
					return null;
				}
				return {
					action,
					column: cell.column,
					row: cell.row,
					rowSpan: cell.rowSpan ?? 1,
					size: cell.size ?? "small",
					mode: cell.mode ?? "column",
				} satisfies FeatureLayoutItem;
			})
			.filter((item): item is FeatureLayoutItem => item !== null);
	}

	function getFeatureGridStyle(category: ToolbarCategory): CSSProperties {
		return {
			"--feature-grid-columns": String(category.featureLayout.columns),
			"--feature-grid-rows": String(category.featureLayout.rows),
		} as CSSProperties;
	}

	function getFeatureCellStyle(item: FeatureLayoutItem): CSSProperties {
		return {
			gridColumn: String(item.column),
			gridRow: `${item.row} / span ${item.rowSpan}`,
		};
	}

	function isCommandActive(command?: string): boolean {
		if (!command) return false;
		return activeCommands.value.includes(command);
	}

	function isFeatureActionActive(action: ToolbarAction): boolean {
		if (action.key === "feature-corner") {
			return isFeatureCornerActive.value;
		}
		if (action.key === "feature-pattern") {
			return isFeaturePatternActive.value;
		}
		if (action.key === "reference-geometry") {
			return isFeatureReferenceActive.value;
		}
		return isCommandActive(action.command);
	}

	function applyFeatureCornerCommand(command: string): void {
		const option = featureCornerCommandMap.get(command);
		if (!option || option.disabled) {
			return;
		}
		activeFeatureCornerMode.value = option.mode;
		emit("command", option.command);
		emit("operationChange", {
			name: option.label,
			hint: option.hint,
		});
	}

	function handleFeatureCornerSelect(key: string): void {
		applyFeatureCornerCommand(key);
	}

	function handleFeatureCornerClick(): void {
		applyFeatureCornerCommand(activeFeatureCornerOption.value.command);
	}

	function applyFeaturePatternCommand(command: string): void {
		const option = featurePatternCommandMap.get(command);
		if (!option || option.disabled) {
			return;
		}
		activeFeaturePatternMode.value = option.mode;
		emit("command", option.command);
		emit("operationChange", {
			name: option.label,
			hint: option.hint,
		});
	}

	function handleFeaturePatternSelect(key: string): void {
		applyFeaturePatternCommand(key);
	}

	function handleFeaturePatternClick(): void {
		applyFeaturePatternCommand(activeFeaturePatternOption.value.command);
	}

	function applyFeatureReferenceCommand(command: string): void {
		const option = featureReferenceCommandMap.get(command);
		if (!option || option.disabled) {
			return;
		}
		activeFeatureReferenceMode.value = option.mode;
		emit("command", option.command);
		emit("operationChange", {
			name: option.label,
			hint: option.hint,
		});
	}

	function handleFeatureReferenceSelect(key: string): void {
		applyFeatureReferenceCommand(key);
	}

	function handleFeatureReferenceClick(): void {
		applyFeatureReferenceCommand(activeFeatureReferenceOption.value.command);
	}

	function handleAction(action: ToolbarAction): void {
		if (action.disabled) {
			return;
		}
		if (action.command) {
			emit("command", action.command);
		}
		emit("operationChange", {
			name: action.label,
			hint: action.hint ?? "正在打开操作，请在视口中选择相关对象。",
		});
	}
</script>

<style scoped lang="less">
	.command-categories {
		display: flex;
		align-items: stretch;
		gap: 0;
		flex-wrap: nowrap;
		width: 100%;
		height: 116px;
		min-height: 116px;
		max-height: 116px;
		box-sizing: border-box;
		overflow-x: auto;
	}

	.category {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 4px 8px;
		min-width: max-content;
		position: relative;
	}

	.category + .category {
		border-left: 1px solid var(--n-tab-border-color);
	}

	.category-actions.feature-layout-grid {
		--feature-large-width: 66px;
		--feature-small-min-width: 62px;
		--feature-row-height: 34px;
		display: grid;
		grid-template-columns: repeat(var(--feature-grid-columns), max-content);
		grid-template-rows: repeat(var(--feature-grid-rows), var(--feature-row-height));
		align-items: stretch;
		justify-items: stretch;
		column-gap: 3px;
		row-gap: 3px;
	}

	.feature-command {
		width: max-content;
		height: 100%;
	}

	.feature-command-small {
		width: max-content;
		min-width: var(--feature-small-min-width);
	}

	.feature-command-large {
		width: var(--feature-large-width);
	}

	.feature-command > * {
		width: 100%;
	}

	:deep(.dropdown-option-icon) {
		width: 18px;
		height: 18px;
		object-fit: contain;
	}
</style>
